import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { useQuery } from '@mint-lib/query-client';
import { AiAssistantSocket, Chat } from '@mint-sdk/api-ai-assistant';

import {
  getChatMessages,
  groupReasoningSteps,
} from '../helpers/chatMessageFormatters.js';
import { useSyncChatHistoryOnEnter } from '../hooks/useSyncChatHistoryOnEnter.jsx';
import { ReasoningStep } from '../types/reasoningSteps.js';
import { ChatTargetProvider } from './ChatTargetProvider.jsx';
import { useSocket } from './SocketProvider.jsx';
import { useTextChatActions } from './useTextActions.jsx';

type SendMessagePayload = {
  text: string;
  metadata?: Chat.UserMetadataSchemaType;
};

type ChatContextValue = {
  // Data
  chatMessages: Chat.ChatMessageRecordType[];
  reasoningMessages: ReasoningStep[];
  isRefetching: boolean;
  isChatHistoryLoading: boolean;

  // Computed state
  isAiProcessing: boolean;
  canSendMessage: boolean;

  // Actions
  sendMessage: (payload: SendMessagePayload) => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);

type ChatProviderProps = {
  children: ReactNode;
  target?: AiAssistantSocket.ChatTarget;
};

const EmptyChatProvider = ({ children }: { children: ReactNode }) => {
  const { sendMessage } = useTextChatActions(undefined);

  const value = useMemo<ChatContextValue>(
    () => ({
      chatMessages: [],
      reasoningMessages: [],
      currentChat: undefined,
      isRefetching: false,
      isAiProcessing: false,
      canSendMessage: true,
      sendMessage,
      isChatHistoryLoading: false,
    }),
    [sendMessage],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

const ActiveChatProvider = ({
  children,
  chatId,
}: PropsWithChildren<{ chatId: string }>) => {
  const {
    data: allMessages = [],
    isRefetching,
    isLoading: isChatHistoryLoading,
  } = useQuery({
    ...Chat.getHistoryById({
      urlParams: { chatSessionId: chatId },
    }),
    staleTime: Infinity,
  });

  useSyncChatHistoryOnEnter(chatId);

  const { sendMessage } = useTextChatActions(chatId);

  const chatMessages = useMemo(
    () => getChatMessages(allMessages),
    [allMessages],
  );

  const reasoningMessages = useMemo(
    () => groupReasoningSteps(allMessages),
    [allMessages],
  );

  const lastMessage = useMemo(() => allMessages.at(-1)?.message, [allMessages]);

  const isLastMessageScheduled = useMemo(() => {
    return lastMessage?.additionalMetadata?.scheduled ?? false;
  }, [lastMessage]);

  const isAiProcessing = useMemo(() => {
    if (!lastMessage) return false;

    const isLastHuman = lastMessage.type === 'human';
    const hasIsLastFlag =
      lastMessage.type === 'ai' &&
      typeof lastMessage.additionalMetadata?.isLast === 'boolean';

    if (hasIsLastFlag) {
      return !lastMessage.additionalMetadata.isLast;
    }

    return (
      isLastHuman &&
      !lastMessage?.additionalMetadata?.cancelled &&
      !isLastMessageScheduled
    );
  }, [lastMessage, isLastMessageScheduled]);

  const canSendMessage = useMemo(() => {
    return !isAiProcessing && !isLastMessageScheduled;
  }, [isAiProcessing, isLastMessageScheduled]);

  const value = useMemo<ChatContextValue>(
    () => ({
      chatMessages,
      reasoningMessages,
      isRefetching,
      isAiProcessing,
      isChatHistoryLoading,
      canSendMessage,
      sendMessage,
    }),
    [
      chatMessages,
      reasoningMessages,
      isRefetching,
      isAiProcessing,
      isChatHistoryLoading,
      canSendMessage,
      sendMessage,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const TextChatProvider = ({ children, target }: ChatProviderProps) => {
  const { chatId } = useSocket();

  return (
    <ChatTargetProvider target={target}>
      {chatId ? (
        <ActiveChatProvider chatId={chatId}>{children}</ActiveChatProvider>
      ) : (
        <EmptyChatProvider>{children}</EmptyChatProvider>
      )}
    </ChatTargetProvider>
  );
};

export const useTextChat = (): ChatContextValue => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('useTextChat must be used within TextChatProvider');
  }

  return context;
};
